import type { Clubhouse_LeagueType } from "../../enums";
import type { Clubhouse_LeagueV2RosterMemberDto } from "../../types/leagueRoster";
import type { Clubhouse_LeagueV2TeamSummaryDto } from "../../types/leagueTeams";
import { fullTimePlayers } from "../teams/teamsBoardModel";
import type { BoardId } from "../teams/teamsBoardModel";
import { isTeamBasedFormat } from "./advancedLeagueSchema";

/** The steps that can hold a blocking problem rather than merely being unfinished. */
export type LeagueStepIssueKey = "roster" | "teams";

/**
 * What would make Finalize fail, per step, phrased for an operator.
 *
 * Deliberately separate from the editor's `tabComplete`: that answers "has this
 * step been filled in", which is false for a step nobody has reached yet. These
 * answer "is what is here wrong", which is the only thing worth colouring red.
 * An untouched step has no issue.
 *
 * The team-count wording matches the server's own rejection text so the operator
 * does not read one sentence here and a different one from the API.
 */
export function deriveLeagueStepIssues({
  leagueType,
  teamSize,
  roster,
  teams,
}: {
  leagueType?: Clubhouse_LeagueType;
  teamSize: number;
  roster: Clubhouse_LeagueV2RosterMemberDto[] | undefined;
  teams: Clubhouse_LeagueV2TeamSummaryDto[] | undefined;
}): Partial<Record<LeagueStepIssueKey, string>> {
  const issues: Partial<Record<LeagueStepIssueKey, string>> = {};
  const players = fullTimePlayers(roster);
  if (players.length === 0) return issues;

  if (!isTeamBasedFormat(leagueType) || teamSize <= 0) return issues;

  const surplus = players.length % teamSize;
  if (surplus !== 0) {
    // One sentence covers both steps: the roster count is the cause and the
    // incomplete team is the symptom, so naming the symptom separately would
    // only give the operator a second thing to read and nothing extra to do.
    const shortfall = teamSize - surplus;
    const fix =
      surplus === shortfall
        ? `Add or remove ${plural(surplus, "player", true)}`
        : `Add ${plural(shortfall, "player", true)} or remove ${plural(surplus, "player", true)}`;
    const message = `Teams require ${teamSize} full-time players. ${fix} to complete the roster.`;
    issues.roster = message;
    issues.teams = message;
    return issues;
  }

  const teamRows = teams ?? [];
  const assigned = new Set<BoardId>(
    teamRows.flatMap((t) =>
      (t.players ?? []).flatMap((p) => (p.userId == null ? [] : [p.userId as BoardId])),
    ),
  );
  const unassigned = players.filter((p) => !assigned.has(p.userId)).length;
  if (unassigned > 0) {
    issues.teams = `${unassigned} ${plural(unassigned, "player")} ${unassigned === 1 ? "is" : "are"} not on a team.`;
    return issues;
  }

  const incomplete = teamRows.filter(
    (t) => (t.players?.length ?? 0) < teamSize,
  ).length;
  if (incomplete > 0) {
    issues.teams = `${incomplete} ${plural(incomplete, "team")} ${incomplete === 1 ? "does" : "do"} not have ${teamSize} players.`;
  }

  return issues;
}

function plural(count: number, noun: string, withCount = false): string {
  const word = count === 1 ? noun : `${noun}s`;
  return withCount ? `${count} ${word}` : word;
}
